// @flow
import React from 'react';
import { Grid, Label, Table } from 'semantic-ui-react';
import { getIn, useFormikContext } from 'formik';

import {
  StyledCheckboxWithAccessibility,
  StyledFormikErrorMessage,
  StyledMonthlyUsageInput,
} from '../../../common-utils/styledComponents';
import { Field } from '../../../formik-utils/formikSUIWrapper';

import { addAdditionalDummyMonthsIfRequired, isMonthLockedByKafkaMonth } from './utils';
import ClusterMonthlyUsageRampCalculator from './ClusterMonthlyUsageRampCalculator';
import ClusterMonthlyBooleanSetAll from './ClusterMonthlyBooleanSetAll';
import { PeakLessThanAvgPopup } from './PeakLessThanAvgPopup';

export const ClusterInputTableRow = ({
  displayName,
  backendName,
  selectedYear,
  months,
  onChangeFunc,
  isBooleanField = false,
  includeSetAllMonthsControl = false,
  monthlyEstimatesFieldName,
  displayNameWidth = 4,
  inputWidth = 1,
  totalNumberOfMonths,
  disabled,
  isReadOnly = false,
  shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled = false,
}) => {
  const monthsToUse = addAdditionalDummyMonthsIfRequired(selectedYear, months);
  const { values: formikValues } = useFormikContext();

  return (
    <Table.Row disabled={disabled}>
      {isBooleanField ? (
        <Table.Cell width={displayNameWidth}>
          {includeSetAllMonthsControl && !isReadOnly ? (
            <Grid>
              <Grid.Row>
                <Grid.Column width={12}>{displayName}</Grid.Column>
                <Grid.Column textAlign="right" width={4}>
                  <ClusterMonthlyBooleanSetAll
                    disabled={disabled}
                    monthlyEstimatesFieldName={monthlyEstimatesFieldName}
                    shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled={
                      shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled
                    }
                    totalNumberOfMonths={totalNumberOfMonths}
                    usageMetric={backendName}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          ) : (
            displayName
          )}
        </Table.Cell>
      ) : (
        <Table.Cell>
          <Grid>
            <Grid.Row>
              <Grid.Column width={isReadOnly ? 16 : 12}>{displayName}</Grid.Column>
              {!isReadOnly && (
                <Grid.Column textAlign="right" width={4}>
                  <ClusterMonthlyUsageRampCalculator
                    monthlyEstimatesFieldName={monthlyEstimatesFieldName}
                    totalNumberOfMonths={totalNumberOfMonths}
                    usageMetric={backendName}
                  />
                </Grid.Column>
              )}
            </Grid.Row>
          </Grid>
        </Table.Cell>
      )}

      {monthsToUse.map((month, index) => {
        const fieldName = `${monthlyEstimatesFieldName}[months][${month.name}].${backendName}`;

        let isPeakLessThanAvgForMonth = false;
        if (fieldName.includes('_peak_')) {
          const correspondingAvgFieldName = fieldName.replace('_peak_', '_avg_');
          const peakValue = getIn(formikValues, fieldName);
          const averageValue = getIn(formikValues, correspondingAvgFieldName);
          isPeakLessThanAvgForMonth = peakValue < averageValue;
        }

        const shouldMonthBeDisabledBasedOnKafkaMonth = isMonthLockedByKafkaMonth(
          formikValues,
          month.name,
          shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled
        );
        // Note, when shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled is true, then, check if the corresponding Kafka month is disabled, if yes, we need to disable this field
        // Note, just disabling the field here and not setting the checkbox value to false that is handled by getDataForOtherTabsThatNeedToBeSentToBackEnd

        // todo:: Use CheckBoxField and TextField here - not used currently because of some CSS issue with FormField

        return (
          <Table.Cell
            disabled={disabled}
            key={index}
            selectable={!isReadOnly}
            style={
              isPeakLessThanAvgForMonth
                ? {
                    border: '2px dotted red',
                  }
                : {}
            }
            textAlign="center"
            width={inputWidth}
          >
            {isReadOnly ? (
              <>{getIn(formikValues, fieldName)}</>
            ) : (
              <>
                {isBooleanField ? (
                  <>
                    <Field
                      component={StyledCheckboxWithAccessibility}
                      disabled={
                        month.isMonthArtificiallyGenerated === true ||
                        shouldMonthBeDisabledBasedOnKafkaMonth ||
                        disabled
                      }
                      name={fieldName}
                      onChange={(event, value, setFieldValue, setFieldTouched, values, checked) =>
                        onChangeFunc(
                          month.name,
                          event,
                          value,
                          setFieldValue,
                          setFieldTouched,
                          values,
                          checked
                        )
                      }
                      toggle={true}
                      type="checkbox"
                    />
                    <StyledFormikErrorMessage
                      basic={true}
                      color="red"
                      component={Label}
                      name={fieldName}
                      pointing={true}
                      showErrorsOnlyWhenTouched={false}
                    />
                  </>
                ) : (
                  <>
                    <Field
                      component={StyledMonthlyUsageInput}
                      disabled={month.isMonthArtificiallyGenerated === true || disabled}
                      name={fieldName}
                      onChange={onChangeFunc}
                      onFocus={(e) => e.target.select()}
                      type="number"
                    />
                    {isPeakLessThanAvgForMonth && <PeakLessThanAvgPopup />}
                    <StyledFormikErrorMessage
                      basic={true}
                      color="red"
                      component={Label}
                      name={fieldName}
                      pointing={true}
                      showErrorsOnlyWhenTouched={false}
                    />
                  </>
                )}
              </>
            )}
          </Table.Cell>
        );
      })}
    </Table.Row>
  );
};
